import { baseApi } from './base';
import type {
  DashboardConfig,
  WidgetMetadata,
  ProfileOption,
  FinancialSummaryResponse,
  CashFlowResponse,
  LawsuitStatsResponse,
  LawsuitStalledResponse,
  TimelineResponse,
  FinancialProgressionResponse,
} from '@/types/dashboard';

export const dashboardService = {
  // Dashboard Endpoints
  async getDashboardConfig(profileId?: string): Promise<DashboardConfig> {
    const query = profileId ? `?profile_id=${profileId}` : '';
    return baseApi.request<DashboardConfig>(`/dashboard/config/${query}`);
  },

  async updateDashboardConfig(data: DashboardConfig, profileId?: string): Promise<DashboardConfig> {
    const body: DashboardConfig & { profile_id?: string } = { ...data };
    if (profileId) {
      body.profile_id = profileId;
    }
    return baseApi.request<DashboardConfig>('/dashboard/config/', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getDashboardWidgets(): Promise<WidgetMetadata[]> {
    return baseApi.request<WidgetMetadata[]>('/dashboard/widgets/');
  },

  async getManageableProfiles(): Promise<ProfileOption[]> {
    return baseApi.request<ProfileOption[]>('/dashboard/profiles/manageable/');
  },

  async getFinancialSummary(): Promise<FinancialSummaryResponse> {
    return baseApi.request<FinancialSummaryResponse>('/dashboard/financial-summary/');
  },

  async getCashFlow(): Promise<CashFlowResponse> {
    return baseApi.request<CashFlowResponse>('/dashboard/cash-flow/');
  },

  async getLawsuitStats(): Promise<LawsuitStatsResponse> {
    return baseApi.request<LawsuitStatsResponse>('/dashboard/lawsuit-stats/');
  },

  async getLawsuitStalled(): Promise<LawsuitStalledResponse> {
    return baseApi.request<LawsuitStalledResponse>('/dashboard/lawsuit-stalled/');
  },

  async getTimeline(params?: { limit?: number; before_date?: string }): Promise<TimelineResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.before_date) qs.set('before_date', params.before_date);
    const query = qs.toString() ? `?${qs}` : '';
    return baseApi.request<TimelineResponse>(`/dashboard/timeline/${query}`);
  },

  async getDashboardEvents(params?: { tab?: 'agenda' | 'my_tasks'; limit?: number; after_date?: string }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.tab) qs.set('tab', params.tab);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.after_date) qs.set('after_date', params.after_date);
    const query = qs.toString() ? `?${qs}` : '';
    return baseApi.request<any>(`/dashboard/events/${query}`);
  },

  async getFinancialProgression(): Promise<FinancialProgressionResponse> {
    return baseApi.request<FinancialProgressionResponse>('/dashboard/financial-progression/');
  },

  async getUnbilledWIP(): Promise<import('@/types/dashboard').UnbilledWIPResponse> {
    return baseApi.request<import('@/types/dashboard').UnbilledWIPResponse>('/dashboard/unbilled-wip/', {}, false, true);
  },

  async getTeamProductivity(): Promise<import('@/types/dashboard').TeamProductivityResponse> {
    return baseApi.request<import('@/types/dashboard').TeamProductivityResponse>('/dashboard/team-productivity/', {}, false, true);
  },

  async getPendingInvoices(): Promise<import('@/types/dashboard').PendingInvoicesResponse> {
    return baseApi.request<import('@/types/dashboard').PendingInvoicesResponse>('/dashboard/pending-invoices/', {}, false, true);
  },
};
